import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DrawerBottomSheet} from '~/CreateFabricInvitation/components/DrawerBottomSheet/DrawerBottomSheet';
import {canvasShowTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {MWEditorDrawerContent} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/Mobile/MWEditorDrawerContent';
import {MWEditorDrawerHeader} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/Mobile/MWEditorDrawerHeader';
import {menuMappingKeys} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectEditingMobile,
  selectFabricArt,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {MENU_TYPES} from './PhotoEditor/contants';
import {PhotoEditorBottomSheet as PhotoEditor} from './PhotoEditor/PhotoEditorBottomSheet';
import {PhotoEditorDrawerHeader} from './PhotoEditor/PhotoEditorDrawerHeader';
import './CardEditorMobile.sass';

export const CardEditorMobile = () => {
  const {canvas, activeObjects} = useContext(EventCreateContext);
  const [showMenu, setShowMenu] = useState(true);
  const activeCardEditor = useSelector(selectActiveCardEditor);
  const fabricArt = useSelector(selectFabricArt);
  const [activeMenu, setActiveMenu] = useState(activeCardEditor);
  const editingMobile = useSelector(selectEditingMobile);
  const dispatch = useDispatch();
  const hasPhotoOption = fabricArt?.objects?.find((obj) => obj.type === 'uYOImage');
  const [isPhotoDrawer, setIsPhotoDrawer] = useState(activeMenu === 'intro' && hasPhotoOption);
  const [designToolMenu, setDesignToolMenu] = useState(MENU_TYPES.MAIN);

  const closeActiveEditor = useCallback(
    () =>
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: false},
      }),
    [dispatch]
  );

  const onCloseEditorClick = useCallback(() => {
    setActiveMenu(activeCardEditor);
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
    setShowMenu(false);
    canvas?.discardActiveObject().renderAll();
    canvasShowTextBordersAndIcons(canvas);
  }, [activeCardEditor, dispatch, canvas]);

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      const step4Scrollable = document.querySelector('.event-create-design');
      const step1Scrollable = document.querySelector('.event-create-editor--card-design');
      const canvasWrapper = document.querySelector('.event-create-design__content');
      const wrapperToScroll = step1Scrollable ? canvasWrapper : step4Scrollable;

      wrapperToScroll.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 1);
  }, []);

  const onClickCloseTabHandler = useCallback(() => {
    closeActiveEditor();
    scrollToTop();
    setActiveMenu('text');
    canvas.renderAll();
  }, [canvas, scrollToTop, closeActiveEditor]);

  // Product is aware of the jank required to do this & the non-ideal nature considering
  // the display differs depending on device resolution & browser chrome, but alas...
  const getDrawerHeight = useMemo(() => {
    const shorterDrawers = ['text', menuMappingKeys.FONT_SIZE];
    if (activeMenu === menuMappingKeys.TEXT_FORMAT) return 25;
    if (activeMenu === menuMappingKeys.FONT_STYLE) return 35;
    if (shorterDrawers.includes(activeMenu)) return 20;
    return 30;
  }, [activeMenu]);

  const scrollToTopCanvas = useCallback(() => {
    const step4Scrollable = document.querySelector('.scrollable');
    const canvasWrapper = document.querySelector('.event-create-design__content');
    if (!activeObjects || !step4Scrollable || !canvasWrapper) return;

    const wrapperToScroll = step4Scrollable || canvasWrapper;
    const y = activeObjects[0].top / 2 - (canvasWrapper?.clientHeight || 0) / 2 - 120;

    wrapperToScroll.scrollTo({
      top: y,
      left: 0,
      behavior: 'smooth',
    });
  }, [activeObjects]);

  const showMobileDrawer = useCallback(() => {
    if (editingMobile && !showMenu && (activeMenu || activeCardEditor)) {
      setShowMenu(true);
    }
  }, [activeMenu, activeCardEditor, showMenu, editingMobile]);

  useEffect(() => {
    setIsPhotoDrawer(!!(['intro', 'image'].includes(activeMenu) && hasPhotoOption));
  }, [activeMenu, hasPhotoOption]);

  useEffect(() => {
    if (!activeObjects) {
      const imgObjs = fabricArt?.objects?.find((obj) => obj.type === 'uYOImage');
      const textObjs = fabricArt?.objects?.find((obj) => obj.type === 'textbox');
      if (imgObjs && activeCardEditor === 'image') setActiveMenu('image');
      if (textObjs && activeCardEditor === 'text') setActiveMenu('text');
    } else {
      const isPhotoSelected = activeObjects?.[0]?.type === 'uYOImage';
      const isTextSelected = activeObjects?.[0]?.type === 'textbox';
      if (isPhotoSelected && !['image', menuMappingKeys.UYO].includes(activeMenu)) {
        setActiveMenu('image');
      }
      if (
        isTextSelected &&
        ![
          'text',
          menuMappingKeys.KEYBOARD,
          menuMappingKeys.SPACING,
          menuMappingKeys.COLOR,
          menuMappingKeys.TEXT_FORMAT,
          menuMappingKeys.TEXT_FORMAT,
          menuMappingKeys.FONT_SIZE,
          menuMappingKeys.FONT_STYLE,
        ].includes(activeMenu)
      ) {
        setActiveMenu('text');
      }
    }
    if (!editingMobile) {
      setShowMenu(editingMobile);
    }
  }, [editingMobile, activeMenu, activeObjects, fabricArt, activeCardEditor]);

  useEffect(() => {
    if (showMenu && activeMenu === menuMappingKeys.FONT_STYLE && !!activeObjects?.length) {
      scrollToTopCanvas();
    }
  }, [activeMenu, activeObjects, showMenu, scrollToTopCanvas]);

  useEffect(() => {
    setActiveMenu(activeCardEditor);
  }, [activeCardEditor]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (editingMobile && !showMenu && (activeMenu || activeCardEditor)) {
      setShowMenu(true);
    }
  }, [activeMenu, activeCardEditor, showMobileDrawer, editingMobile, showMenu]);

  if (!editingMobile) {
    return null;
  }

  if (isPhotoDrawer) {
    return (
      <DrawerBottomSheet
        isOpen={showMenu}
        onClose={onCloseEditorClick}
        className="photo-editor"
        initHeight="auto"
        header={
          <PhotoEditorDrawerHeader
            activeDesignToolMenu={designToolMenu}
            setActiveDesignToolMenu={setDesignToolMenu}
            scrollToTop={scrollToTop}
            setShowMenu={setShowMenu}
          />
        }
        minHeightPercentage={getDrawerHeight}
      >
        <PhotoEditor
          scrollToTop={scrollToTop}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
          uyoImage={activeObjects?.[0]}
          activeDesignToolMenu={designToolMenu}
          setActiveDesignToolMenu={setDesignToolMenu}
        />
      </DrawerBottomSheet>
    );
  }

  return (
    <DrawerBottomSheet
      isOpen={showMenu}
      onClose={onCloseEditorClick}
      className="mobile-editor"
      header={
        <MWEditorDrawerHeader
          activeMenu={activeMenu}
          showMenu={showMenu}
          onCloseEditorClick={onCloseEditorClick}
          onClickCloseTabHandler={onClickCloseTabHandler}
          isPhotoDrawer={isPhotoDrawer}
          editingMobile={editingMobile}
        />
      }
      minHeightPercentage={getDrawerHeight}
    >
      <MWEditorDrawerContent
        activeMenu={activeMenu}
        showMenu={showMenu}
        scrollToTop={scrollToTop}
        setActiveMenu={setActiveMenu}
        setShowMenu={setShowMenu}
        isPhotoDrawer={isPhotoDrawer}
      />
    </DrawerBottomSheet>
  );
};
